/**
 * Oynatmanın başlayacağı konum.
 *
 * ## Neden ayrı bir yardımcı?
 *
 * `WatchScreen` ve `usePlaybackPlan` aynı soruyu iki kez soruyordu:
 * "kaldığı yerden devam et" konumu nedir? İki kopya ayrışırsa oynatıcı bir
 * konumdan başlar, karar ucu başka bir konumu bildirir.
 *
 * ## Bulunan hata (müzikte zincirleme "sıradaki parça")
 *
 * İzlenmiş bir parçanın `progress.positionMs` değeri **dosyanın süresine
 * eşittir** (kapanış raporu son konumu yazar). Oynatıcı bu değeri "kaldığın
 * yer" sanıp `currentTime = duration` yapıyor; `ended` bir anda tetikleniyor,
 * `useAutoAdvance` sıradaki parçaya geçiyor, o da aynı sona atlıyor ve
 * kütüphane baştan sona saniyeler içinde taranıyor. Her parça "izlendi"
 * işaretleniyor.
 *
 * Kural:
 *   1. **İzlenmiş** içerik baştan başlar.
 *   2. Kayıtlı konum sürenin **son 5 saniyesindeyse** baştan başlar
 *      (bitirmek üzere bırakılmış içerik).
 *   3. Aksi hâlde kayıtlı konum korunur.
 */

/** Sürenin sonuna bu kadar kaldıysa "bitmiş" sayılır. */
export const NEAR_END_MS = 5_000;

export interface ResumeInput {
  /** İçerik daha önce bitirilmiş mi? */
  isWatched: boolean;
  /** Katalog süresi; bilinmiyorsa `null`. */
  durationMs: number | null;
  /** Sunucudan gelen kayıtlı ilerleme. */
  progress: { positionMs: number; updatedAt?: number } | null;
  /** Yalnız "devam et" yanıtında gelen konum. */
  resumePositionMs?: number | undefined;
}

export interface ResumeOptions {
  /**
   * Müzikte parça daha önce tamamen dinlenmiş olsa bile sonradan oluşan
   * ilerlemeyi kullan. Tekrar dinlemelerde en güncel konum beklenir.
   */
  ignoreWatchedForMusic?: boolean;
}

/**
 * Başlangıç konumu (ms). Her durumda negatif olmayan bir sayı döner.
 */
export function resumePositionMs(item: ResumeInput, options: ResumeOptions = {}): number {
  // Bitirilmiş içerik baştan oynar. Aksi hâlde "sona at → anında bit"
  // döngüsü oluşur (bkz. dosya başlığı). Müzikte sonradan oluşan ilerleme
  // bu kuralın dışındadır.
  if (item.isWatched && options.ignoreWatchedForMusic !== true) return 0;

  const raw = item.resumePositionMs ?? item.progress?.positionMs ?? 0;
  if (!Number.isFinite(raw) || raw <= 0) return 0;

  const duration = item.durationMs ?? 0;
  if (duration > 0 && raw >= duration - NEAR_END_MS) return 0;

  return raw;
}
